/**
 * Body composition: the US Navy tape-measure estimate, lean-mass arithmetic, and the latest
 * measurement from the weigh-in log.
 *
 * Navy equations: Hodgdon JA, Beckett MB. Prediction of percent body fat for U.S. Navy men and
 * women from body circumferences and height. Naval Health Research Center reports 84-11 (men) and
 * 84-29 (women), San Diego, 1984. Inch form as used by DoD Instruction 1308.3 (2002):
 *   men    %BF = 86.010 x log10(abdomen - neck) - 70.041 x log10(height) + 36.76
 *   women  %BF = 163.205 x log10(waist + hip - neck) - 97.684 x log10(height) - 78.387
 * Tape sites: neck just below the larynx; men's abdomen at the navel; women's waist at the
 * narrowest point and hips at the widest.
 */
import type { BodyStat, BodyStatSource, Sex } from './types';

export const CM_PER_IN: number = 2.54;

export interface NavyInput {
  sex: Sex;
  heightCm: number;
  neckCm: number;
  /** Men: abdomen at the navel. Women: natural waist (narrowest point). */
  waistCm: number;
  /** Women only: widest point of the hips. */
  hipCm?: number | null;
}

export type NavyResult = { ok: true; bodyFatPct: number } | { ok: false; reason: string };

export function navyBodyFat(i: NavyInput): NavyResult {
  const h: number = i.heightCm / CM_PER_IN;
  const n: number = i.neckCm / CM_PER_IN;
  const w: number = i.waistCm / CM_PER_IN;
  if (!(h > 0) || !(n > 0) || !(w > 0)) return { ok: false, reason: 'Enter your height, neck and waist.' };
  let bf: number;
  if (i.sex === 'male') {
    if (w - n <= 0) return { ok: false, reason: 'The waist (at the navel) has to measure more than the neck.' };
    bf = 86.010 * Math.log10(w - n) - 70.041 * Math.log10(h) + 36.76;
  } else {
    const hip: number = (i.hipCm ?? 0) / CM_PER_IN;
    if (!(hip > 0)) return { ok: false, reason: 'The women\'s equation also needs the hip measurement.' };
    if (w + hip - n <= 0) return { ok: false, reason: 'Waist plus hips has to measure more than the neck.' };
    bf = 163.205 * Math.log10(w + hip - n) - 97.684 * Math.log10(h) - 78.387;
  }
  if (!Number.isFinite(bf) || bf < 2 || bf > 75) {
    return { ok: false, reason: `That works out to ${Number.isFinite(bf) ? bf.toFixed(1) : '?'}%, outside what the tape method can estimate. Re-check the measurements.` };
  }
  return { ok: true, bodyFatPct: Math.round(bf * 10) / 10 };
}

export const leanFromBodyFat = (weightKg: number, bodyFatPct: number): number => weightKg * (1 - bodyFatPct / 100);
export const bodyFatFromLean = (weightKg: number, leanKg: number): number => 100 * (1 - leanKg / weightKg);

/** Latest weigh-in plus the latest body-composition measurement. */
export interface BodyComp {
  weightKg: number | null;
  weightDate: string | null;
  /** Body fat % of the latest measurement (measured, or derived from a lean-mass measurement). */
  bodyFatPct: number | null;
  /** Lean mass now: the measured value, else weight x (1 - body fat %). */
  leanMassKg: number | null;
  compDate: string | null;
  compSource: BodyStatSource | 'lifting_profile' | null;
  /** True when lean mass was measured rather than worked out from a body-fat %. */
  leanMeasured: boolean;
}

/**
 * From weigh-ins newest first. A body-fat % is applied to the current weight (the trajectory engine
 * does the same); a measured lean mass is kept as measured. `liftingBodyFatPct` (set in Mighty
 * Peanut Lifting) is only used when the log has no measurement.
 */
export function bodyCompFrom(statsNewestFirst: ReadonlyArray<BodyStat>, liftingBodyFatPct: number | null = null): BodyComp {
  const latest: BodyStat | undefined = statsNewestFirst[0];
  const weightKg: number | null = latest?.weight_kg ?? null;
  const comp: BodyStat | undefined = statsNewestFirst.find((s) => s.body_fat_pct !== null || s.lean_mass_kg !== null);
  const out: BodyComp = { weightKg, weightDate: latest?.local_date ?? null, bodyFatPct: null, leanMassKg: null, compDate: null, compSource: null, leanMeasured: false };
  if (comp) {
    out.compDate = comp.local_date;
    out.compSource = comp.source;
    if (comp.lean_mass_kg !== null) {
      out.leanMassKg = comp.lean_mass_kg;
      out.leanMeasured = true;
      out.bodyFatPct = comp.body_fat_pct ?? Math.round(bodyFatFromLean(comp.weight_kg, comp.lean_mass_kg) * 10) / 10;
    } else if (comp.body_fat_pct !== null) {
      out.bodyFatPct = comp.body_fat_pct;
      out.leanMassKg = weightKg !== null ? leanFromBodyFat(weightKg, comp.body_fat_pct) : null;
    }
  } else if (liftingBodyFatPct !== null && liftingBodyFatPct >= 2 && liftingBodyFatPct <= 75) {
    out.bodyFatPct = liftingBodyFatPct;
    out.leanMassKg = weightKg !== null ? leanFromBodyFat(weightKg, liftingBodyFatPct) : null;
    out.compSource = 'lifting_profile';
  }
  return out;
}
